// Policy documents for agents whose editor is built from the server catalog.
// Controls are the document itself plus the pointers dropped on load because
// the current catalog no longer accepts them.

import { pointerToken } from "./documentDiff.js";

const clone = (value) => JSON.parse(JSON.stringify(value));
const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const tokens = (pointer) => pointer.slice(1).split("/").map((token) => token.replace(/~1/g, "/").replace(/~0/g, "~"));

export function valueAt(document, pointer) {
    return tokens(pointer).reduce((value, token) => value?.[token], document);
}

export function setValueAt(document, pointer, value) {
    const next = clone(document);
    const path = tokens(pointer);
    let parent = next;
    for (const token of path.slice(0, -1)) {
        if (!isObject(parent[token])) parent[token] = {};
        parent = parent[token];
    }
    if (value === undefined) delete parent[path.at(-1)];
    else parent[path.at(-1)] = value;
    for (let depth = path.length - 1; depth > 0; depth -= 1) {
        const ancestor = valueAt(next, `/${path.slice(0, depth).map(pointerToken).join("/")}`);
        if (isObject(ancestor) && !Object.keys(ancestor).length) {
            const owner = depth === 1 ? next : valueAt(next, `/${path.slice(0, depth - 1).map(pointerToken).join("/")}`);
            delete owner[path[depth - 1]];
        }
    }
    return next;
}

export function cloneCatalogControls(controls = { document: {}, removedPointers: [] }) {
    return { document: clone(controls.document), removedPointers: [...controls.removedPointers] };
}

export function catalogControlsFromDocument(document, catalog) {
    const accepted = new Map((catalog?.settings ?? []).map((setting) => [setting.pointer, setting]));
    const removedPointers = [];
    const visit = (value, pointer) => {
        const entry = accepted.get(pointer);
        if (!entry) {
            removedPointers.push(pointer);
            return undefined;
        }
        if (!isObject(value)) return clone(value);
        if (entry.valueKind === "MANAGED_AGENT_CONFIG_VALUE_KIND_FEATURE_PINS") {
            const allowed = new Set(entry.allowedValues);
            const kept = {};
            for (const [name, pin] of Object.entries(value)) {
                if (allowed.has(name)) kept[name] = pin;
                else removedPointers.push(`${pointer}/${pointerToken(name)}`);
            }
            return Object.keys(kept).length ? kept : undefined;
        }
        if (entry.valueKind !== "MANAGED_AGENT_CONFIG_VALUE_KIND_OBJECT") return clone(value);
        const kept = {};
        for (const [name, child] of Object.entries(value)) {
            const childPointer = `${pointer}/${pointerToken(name)}`;
            const acceptedChild = visit(child, childPointer);
            if (acceptedChild !== undefined) kept[name] = acceptedChild;
        }
        return Object.keys(kept).length ? kept : undefined;
    };
    const clean = {};
    for (const [name, value] of Object.entries(isObject(document) ? document : {})) {
        const pointer = `/${pointerToken(name)}`;
        const acceptedValue = catalog?.settings?.length ? visit(value, pointer) : clone(value);
        if (acceptedValue !== undefined) clean[name] = acceptedValue;
    }
    return { document: clean, removedPointers };
}
